import { useEffect, useMemo, useState } from 'react';
import { LockFill, ExclamationCircle, Globe } from 'react-bootstrap-icons';
import { useParams } from 'react-router-dom';

import { List, Typography } from 'antd';

import CommentItem from '@/components/shared/CommentItem';
import { useAppSelector } from '@/hooks/redux';
import { enCommentStatus, IComment } from '@/interfaces/comments';
import IUser from '@/interfaces/user';
import { commentsApi, useGetConfigCommentsQuery, useSaveReplyMutation } from '@/services/comments';
import { useGetCourseQuery } from '@/services/course';
import styled, { StyledProp } from '@/styled';

import Delete from './Delete';
import ReplyForm, { CommentFormValues } from '../../Form';

export type Mention = Pick<IUser, 'name' | 'id'>;
interface ActionProps {
  isReply: boolean;
  isOwnComments?: boolean;
}

const statusMapper = {
  [enCommentStatus.waitingAproval]: (
    <div className='comment-status-icons'>
      <ExclamationCircle /> Aguardando moderação
    </div>
  ),
  [enCommentStatus.private]: (
    <div className='comment-status-icons'>
      <LockFill />
      Comentário privado
    </div>
  ),
  [enCommentStatus.public]: (
    <div className='comment-status-icons'>
      <Globe /> Comentário público
    </div>
  )
};

const Actions = (props: IComment & StyledProp & ActionProps) => {
  const { id, hash } = useParams();
  const [answer, setAnswer] = useState(false);
  const [showReplies, setShowReplies] = useState(false);
  const [saveReply, reply] = useSaveReplyMutation();
  const user = useAppSelector(({ auth }) => auth.user);
  const [mention, setMention] = useState<Mention>();

  const [getReplies, replies] = commentsApi.endpoints.getReplies.useLazyQuery();
  const { data: producerId } = useGetCourseQuery(hash, {
    selectFromResult: result => ({ ...result, data: result.data?.producer?.id })
  });
  const config = useGetConfigCommentsQuery({ lessonId: id, producerId });
  const canReply = useMemo(() => {
    if (config.isUninitialized) {
      return false;
    }

    const isReplyStudentEnabled = config.data?.replyStudentAll || config.data?.replyStudent;
    const isSameUser = props?.user?.id === user?.id;
    return isReplyStudentEnabled || isSameUser;
  }, [config, props?.user?.id, user?.id]);

  const fetchNewPage = () => {
    const page = replies.data?.page.page ?? 0;
    const isLastPage = replies.data?.total_pages === page;

    if (isLastPage || replies.isFetching) {
      return;
    }

    const params = { ...replies.data?.page, page: page + 1, size: page === 0 ? 3 : 10 };

    if (page === 1 && replies.data?.page?.size === 3) {
      params.page = 1;
      params.size = 10;
    }

    getReplies({
      lessonId: id,
      parentId: props.id,
      params
    });
  };
  const toggleAnswer = () => {
    setAnswer(isActive => !isActive);
    setMention({ id: props.user.id, name: props.user.name });
  };
  const onFinishReply = (values: CommentFormValues) => {
    return saveReply({ lessonId: id, parentId: props.id, comment: values.message }).then(() => {
      setAnswer(false);
      setMention(null);
    });
  };

  const handleReplyText = useMemo(() => {
    const page = replies.data?.page.page ?? 0;
    const total = props.totalReplies ?? 0;
    const listTotal = replies.data?.data.length ?? 0;
    const isLastPage = replies.data?.total_pages === page;

    if (isLastPage && showReplies) {
      return 'Ocultar comentários';
    }

    if (!isLastPage && showReplies) {
      return `Ver comentários (${total - listTotal})`;
    }

    if (!showReplies) {
      return `Ver comentários (${total})`;
    }
  }, [props.totalReplies, replies.data?.data.length, replies.data?.page.page, replies.data?.total_pages, showReplies]);

  const handleShowReplies = () => {
    const page = replies.data?.page.page ?? 0;
    const isLastPage = replies.data?.total_pages === page;

    setShowReplies(isShowing => {
      if (isShowing === false || !isLastPage) {
        fetchNewPage();
        return true;
      }

      return false;
    });
  };

  useEffect(() => {
    setShowReplies(false);
  }, [props.isOwnComments]);
  return (
    <div className={props.className}>
      <div className='buttons'>
        {props?.totalReplies > 0 && (
          <Typography.Text onClick={handleShowReplies} data-disabled={replies.isFetching} strong>
            {handleReplyText}
          </Typography.Text>
        )}

        {canReply && !props.isReply && (
          <Typography.Text onClick={toggleAnswer} strong>
            Responder
          </Typography.Text>
        )}

        <Delete {...props} />

        {statusMapper[props?.status]}
      </div>
      {answer && (
        <ReplyForm
          mention={mention}
          removeMention={() => setMention(undefined)}
          className='action'
          onFinish={onFinishReply}
          isLoading={reply.isLoading}
        />
      )}
      {showReplies && (
        <List
          loading={replies.isFetching}
          locale={{ emptyText: 'Nenhum comentário encontrado' }}
          dataSource={replies.data?.data}
          renderItem={comment => (
            <CommentItem key={props.id} comment={comment}>
              <Actions {...comment} isReply={true} />
            </CommentItem>
          )}
        />
      )}
    </div>
  );
};

export default styled(Actions)`
  .buttons {
    display: flex;
    flex-wrap: wrap;

    & > span:not(:first-child):before {
      margin: ${({ theme }) => theme.space.spacing(0, 2)};
      content: '•';
    }
  }

  .action {
    margin-top: ${({ theme }) => theme.space.pxToRem(16)};
  }

  .ant-typography {
    cursor: pointer;
  }

  &.skeleton-replies {
    h3 {
      display: none;
    }

    .ant-skeleton-paragraph {
      margin-block-start: 12px;
    }
  }

  button {
    width: fit-content;
  }

  .comment-status-icons {
    margin-left: ${({ theme }) => theme.space.pxToRem(16)};
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: auto;
  }
`;
